import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { logout } from '@/services/auth';
import { geospatialService } from '@/services/geospatial';
import { tileService } from '@/services/tiles';
import { fsiService, BasicFSIResponse, DetailedFSIResponse } from '@/services/fsi';
import { LogOut, Search, Layers, ChevronDown, Box, Map as MapIcon, Compass, X } from 'lucide-react';
import FSIAnalysisModal from '@/components/FSIAnalysisModal';
import DetailedFSIModal from '@/components/DetailedFSIModal';
import CustomFSIReportWizard from '@/components/CustomFSIReportWizard';
import MapLegend from '@/components/MapLegend';
import { generatePopupHTML } from '@/components/PopupComponent';
import 'mapbox-gl/dist/mapbox-gl.css';

// Set Mapbox access token
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
if (!MAPBOX_TOKEN) {
  console.error('Mapbox Access Token is missing! Check your .env file.');
}
mapboxgl.accessToken = MAPBOX_TOKEN || '';

// Land use color mapping based on reser_type and reser_use
const getLandUseColorExpression = () => {
  // This creates a Mapbox expression that evaluates on the GPU
  // Returns color based on reser_type and reser_use properties
  return [
    'case',
    // Educational - Blue (#3498DB)
    ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']], 
      ['literal', ['education', 'school', 'library', 'college', 'technical institute']]],
    '#3498DB',
    ['in', ['downcase', ['coalesce', ['get', 'reser_type'], '']], 
      ['literal', ['education', 'school', 'library']]],
    '#3498DB',
    
    // Healthcare - Green (#2ECC71)
    ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']], 
      ['literal', ['health', 'medical', 'hospital', 'dispensary', 'clinic']]],
    '#2ECC71',
    ['in', ['downcase', ['coalesce', ['get', 'reser_type'], '']], 
      ['literal', ['health', 'medical', 'hospital']]],
    '#2ECC71',
    
    // Commercial - Red (#FF6B6B)
    ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']], 
      ['literal', ['commercial', 'market', 'shopping', 'shop', 'sale', 'business']]],
    '#FF6B6B',
    ['in', ['downcase', ['coalesce', ['get', 'reser_type'], '']], 
      ['literal', ['commercial', 'market', 'shopping']]],
    '#FF6B6B',
    
    // Industrial - Purple (#9B59B6)
    ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']], 
      ['literal', ['industrial', 'factory', 'manufacturing']]],
    '#9B59B6',
    ['in', ['downcase', ['coalesce', ['get', 'reser_type'], '']], 
      ['literal', ['industrial', 'factory']]],
    '#9B59B6',
    
    // Recreation - Dark Green (#27AE60)
    ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']], 
      ['literal', ['recreation', 'park', 'garden', 'play', 'sports', 'swimming']]],
    '#27AE60',
    ['in', ['downcase', ['coalesce', ['get', 'reser_type'], '']], 
      ['literal', ['recreation', 'park', 'garden']]],
    '#27AE60',
    
    // Public Services - Orange (#E67E22)
    ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']], 
      ['literal', ['police', 'fire', 'municipal', 'public purpose', 'civic', 'community']]],
    '#E67E22',
    ['in', ['downcase', ['coalesce', ['get', 'reser_type'], '']], 
      ['literal', ['police', 'fire', 'municipal', 'public purpose']]],
    '#E67E22',
    
    // Infrastructure - Gray (#95A5A6)
    ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']], 
      ['literal', ['electric', 'water', 'drainage', 'utility', 'substation']]],
    '#95A5A6',
    ['in', ['downcase', ['coalesce', ['get', 'reser_type'], '']], 
      ['literal', ['electric', 'water', 'utility']]],
    '#95A5A6',
    
    // Transportation - Amber (#F39C12)
    ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']], 
      ['literal', ['bus', 'parking', 'transport', 'terminal', 'taxi']]],
    '#F39C12',
    ['in', ['downcase', ['coalesce', ['get', 'reser_type'], '']], 
      ['literal', ['bus', 'parking', 'transport']]],
    '#F39C12',
    
    // Residential/Housing - Moccasin (#FFE4B5)
    ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']], 
      ['literal', ['housing', 'residential', 'quarters', 'slum']]],
    '#FFE4B5',
    ['in', ['downcase', ['coalesce', ['get', 'reser_type'], '']], 
      ['literal', ['housing', 'residential', 'quarters']]],
    '#FFE4B5',
    
    // Default - Light gray (#E8F5E8)
    '#E8F5E8'
  ];
};

// Survey zone color mapping based on planning zones
const getSurveyZoneColorExpression = (): any => {
  return [
    'case',
    ['boolean', ['feature-state', 'selected'], false],
    '#FFD700',  // Gold when selected
    
    // Residential Zones (R1, R2, R3, R4, R5) - Yellow/Orange tones
    ['in', ['upcase', ['coalesce', ['get', 'zone'], '']], 
      ['literal', ['R', 'R1', 'R2', 'R3', 'R4', 'R5', 'RESIDENTIAL ZONE', 'RESIDENTIAL ZONE - R1', 'RESIDENTIAL ZONE - R4']]],
    '#FFE4B5',  // Moccasin
    ['in', ['get', 'zone'], 
      ['literal', ['Residential Zone I - R1', 'Residential Zone I -R1', 'Residential Zone I_R1', 'Residential Zone I- R1', 'Residential Zone I-R1', 
                   'Residential Zone II - R2', 'Residential Zone II -R2', 'Residential Zone II-R2',
                   'Residential Zone III - R3', 'Residential Zone III -R3', 'Residential Zone III-R3',
                   'Residential Zone-R1', 'Residentidal_Zone_I', 'Residential R1', 'Residential R2', 'Residential R3']]],
    '#FFE4B5',
    
    // Residential Affordable Housing (RAH) - Light Orange
    ['in', ['upcase', ['coalesce', ['get', 'zone'], '']], 
      ['literal', ['RAH', 'RAH-1', 'TZ+RAH']]],
    '#FFD4A3',
    ['in', ['get', 'zone'], 
      ['literal', ['Residential Affordable Housing Zone - RAH', 'Residential Affordable Housing Zone-RAH', 'Residential affordable Hosing Zone-RAH']]],
    '#FFD4A3',
    
    // Agricultural Zones (AG, AG1, A1, A2) - Light Green
    ['in', ['upcase', ['coalesce', ['get', 'zone'], '']], 
      ['literal', ['AG', 'AG-1', 'AG1', 'NON AG']]],
    '#C8E6C9',
    ['in', ['get', 'zone'], 
      ['literal', ['AGRICULTURE ZONE - AG1', 'GENERAL AGRICULTURE ZONE - AG1', 'PRIME AGRICULTURE ZONE - AG2',
                   'General Agricultural Zone - A1', 'General Agricultural Zone -A1', 'General Agricultural Zone-A1',
                   'General Agriculture - A1', 'General Agriculture - A2', 'General_Agricultural_Zone',
                   'Prime Agricultural Zone - A2', 'Prime Agricultural Zone - AG1', 'Prime Agricultural Zone -A1', 
                   'Prime Agricultural Zone -A2', 'Prime Agricultural Zone-A1', 'Prime Agricultural Zone-A2',
                   'Prime Agriculture Zone', 'Prime Agriculture Zone - A2', 'Prime Agriculture Zone -A2',
                   'Prime Agricultural A2', 'A2_Prime_Agricultural_Zone',
                   'Prop General Agricultural Zone -A1', 'Prop. General Agriculture Zone - A1',
                   'Prop.General Agricultural Zone -A1', 'PROP.MODI.AGRICULTURE ZONE - AG1',
                   'Prop. Modi. General Agricultural Zone - A1', 'Prop.Prime Agricultural Zone-A2',
                   'Proposed AG1', 'Bean cultivation']]],
    '#C8E6C9',
    
    // Gamtal & Village (GM, GME) - Beige
    ['in', ['upcase', ['coalesce', ['get', 'zone'], '']], 
      ['literal', ['GM', 'GME', 'GAMTAL', 'VILLAGE']]],
    '#F5DEB3',
    ['in', ['get', 'zone'], 
      ['literal', ['Gamtal', 'Gamtal - GM', 'Gamtal-GM', 'Gamtal Extension - GME', 'Gamtal Extension -GME',
                   'Gamtal Extension-GM', 'Gamtal Extension-GME', 'Gamtal Extenstion-GME', 'Gamtal Extention',
                   'Gamtal Extention - GME', 'GME_Gamtal_Extension', 'village', 'Village',
                   'Prop. Gamtal -GM', 'Prop. Gamtal Extension-GME', 'Prop.Gamtal Extension-GME',
                   'NATURAL GROWTH OF GAMTAL - NGOG', 'NGOG', 'Nishal',
                   'PROP. MODI. NATURAL GROWTH OF GAMTAL', 'PROP. MODI. NATURAL GROWTH OF GAMTAL - NGOG',
                   'PROP.MODI. NATURAL GROWTH OF GAMTAL - NGOG', 'PROP.MODI.NATURAL GROWTH OF GAMTAL - NGOG',
                   'Proposed NGOG']]],
    '#F5DEB3',
    
    // Commercial (C, C2, C3) - Red
    ['in', ['upcase', ['coalesce', ['get', 'zone'], '']], 
      ['literal', ['C3']]],
    '#FF6B6B',
    ['in', ['get', 'zone'], 
      ['literal', ['Commercial Zone - C', 'COMMERCIAL ZONE - C2', 'Commercial Zone -C', 'Commercial Zone-C',
                   'Central Business District - CBD', 'Central Business District- CBD']]],
    '#FF6B6B',
    
    // Industrial (IG, I, I3, GIDC) - Purple
    ['in', ['upcase', ['coalesce', ['get', 'zone'], '']], 
      ['literal', ['I', 'I3', 'IG', 'GIDC-2']]],
    '#9B59B6',
    ['in', ['get', 'zone'], 
      ['literal', ['Industrial Zone general - IG', 'Industrial Zone General - IG', 'Industrial Zone General -IG',
                   'Industrial Zone General IG', 'Industrial Zone General-IG', 'Industrial Zone Special-IS',
                   'Proposed GIDC-2']]],
    '#9B59B6',
    
    // Institutional/Knowledge (I3, KZ) - Blue
    ['in', ['get', 'zone'], 
      ['literal', ['INSTITUTIONAL ZONE - I3', 'Knowledge & Institiuional Zone -KZ', 'Knowledge & Institutional Zone - KZ',
                   'Knowledge & Institutional Zone -KZ', 'Knowledge & Institutional Zone-KZ', 
                   'Knowledge and Instituitional KZ', 'Knowledge_Institutional Zone -KZ', 'Knowledge, Institutional Zone-KZ',
                   'PROP.MODI.INSTITUTIONAL ZONE']]],
    '#3498DB',
    
    // Logistics (L) - Brown
    ['in', ['upcase', ['coalesce', ['get', 'zone'], '']], 
      ['literal', ['L']]],
    '#8D6E63',
    ['in', ['get', 'zone'], 
      ['literal', ['Logistic Zone - L', 'Logistics Zone - L', 'Logistics Zone -L', 'logistics Zone-L', 
                   'Logistics Zone-L', 'Logstic Zone - L']]],
    '#8D6E63',
    
    // Transit Oriented (TOD, TZ) - Cyan
    ['in', ['get', 'zone'], 
      ['literal', ['Transit Oriented Zone - TOD', 'Transit Oriented Zone - TZ', 'Transit Oriented Zone-TZ']]],
    '#00BCD4',
    
    // Parks & Recreation (P, RE) - Dark Green
    ['in', ['upcase', ['coalesce', ['get', 'zone'], '']], 
      ['literal', ['RE1']]],
    '#27AE60',
    ['in', ['get', 'zone'], 
      ['literal', ['Parks, Gardend & Oen Space -P', 'Parks, Gardens & Open Saces -P', 'Parks, Gardens & Open space - P',
                   'Parks, Gardens & Open Space -P', 'Parks,Gardens&Open Space-P',
                   'PROP.MODI.RECREATIONAL ZONE - RE2', 'Gauchar', 'Gaucher', 'Goucher']]],
    '#27AE60',
    
    // Roads & Infrastructure - Gray
    ['in', ['upcase', ['coalesce', ['get', 'zone'], '']], 
      ['literal', ['ROAD', 'WAY']]],
    '#95A5A6',
    ['in', ['get', 'zone'], 
      ['literal', ['road', 'Road', 'Existing Road', 'Existing_road', 'way', 'Way', 'P. Kh. the way',
                   'Proposed DFC', 'Prop. Proposed DFC']]],
    '#95A5A6',
    
    // Water Bodies - Blue
    ['in', ['get', 'zone'], 
      ['literal', ['lake', 'Lake', 'waer body', 'water body', 'waterbody', 'Waterbody', 'WATERBODY',
                   'Sewage / Water Treatment Plant', 'Sewage/ Water Treatent Plant', 'Sewage/Water Treatment',
                   'Sewage/Water Treatment Plant']]],
    '#2196F3',
    
    // Defence & Special Zones - Dark Gray
    ['in', ['upcase', ['coalesce', ['get', 'zone'], '']], 
      ['literal', ['DEFENCE ZONE']]],
    '#424242',
    ['in', ['get', 'zone'], 
      ['literal', ['Special Planned Development - SPD', 'High Flood Hazard']]],
    '#424242',
    
    // Burial/Cremation - Dark Brown
    ['in', ['get', 'zone'], 
      ['literal', ['Burial & Cremation Ground', 'scratch']]],
    '#5D4037',
    
    // Proposed/Modified Zones - Light variations
    ['in', ['get', 'zone'], 
      ['literal', ['Prop. Modi. Gamtal Extension - GME', 'PROP. MODI. RESIDENTIAL ZONE - R1', 
                   'PROP. MODI. RESIDENTIAL ZONE - R3', 'PROP.MODI. RESIDENTIAL ZONE - R3',
                   'Prop. Residential Zone I -R1', 'Prop. Residential Zone I-R1',
                   'Prop.Residential Zone I -R1']]],
    '#E1BEE7',
    
    // Default - Light Gray for unmapped zones
    '#E0E0E0'
  ];
};

const MapLibreMapPage: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const currentPopup = useRef<mapboxgl.Popup | null>(null);
  const selectedFeatureRef = useRef<{ id: string | number; source: string; sourceLayer: string } | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  
  // Layer visibility controls
  const [showLayers, setShowLayers] = useState(false);
  const [layerVisibility, setLayerVisibility] = useState({
    tpsBoundaries: true,
    fpsPlots: true,
    roads: true,
    surveyPlots: true,
    buildings3D: true
  });
  const [is3DView, setIs3DView] = useState(false);
  
  // Search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // FSI Analysis Modal
  const [showFSIModal, setShowFSIModal] = useState(false);
  const [fsiData, setFsiData] = useState<BasicFSIResponse | null>(null);
  const [isLoadingFSI, setIsLoadingFSI] = useState(false);
  const [fsiError, setFsiError] = useState<string | null>(null);
  
  // Detailed FSI Analysis Modal
  const [showDetailedFSIModal, setShowDetailedFSIModal] = useState(false);
  const [detailedFsiData, setDetailedFsiData] = useState<DetailedFSIResponse | null>(null);
  const [isLoadingDetailedFSI, setIsLoadingDetailedFSI] = useState(false);
  const [detailedFsiError, setDetailedFsiError] = useState<string | null>(null);
  const [activeDetailedTab, setActiveDetailedTab] = useState<string>('executive-summary');
  
  // Loading state tracking for specific plots
  const [loadingBasicFSI, setLoadingBasicFSI] = useState<Set<string>>(new Set());
  const [loadingDetailedFSI, setLoadingDetailedFSI] = useState<Set<string>>(new Set());
  
  // Spatial visualization state
  const spatialMapContainer = useRef<HTMLDivElement>(null);
  const spatialMap = useRef<mapboxgl.Map | null>(null);
  const [spatialMapLoaded, setSpatialMapLoaded] = useState(false);
  
  // Plot state for popup functionality
  const [selectedPlot, setSelectedPlot] = useState<any>(null);

  // Custom FSI Report Wizard state
  const [showCustomFSIWizard, setShowCustomFSIWizard] = useState(false);
  const [customFSIGid, setCustomFSIGid] = useState<number | null>(null);

  // Map style state
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v12');
  const [showStyleSelector, setShowStyleSelector] = useState(false);

  // Global functions for popup buttons
  useEffect(() => {
    (window as any).selectPlot = async (gid: string) => {
      try {
        const plot = await geospatialService.getFPSByGID(gid);
        setSelectedPlot(plot);
      } catch (error) {
        console.error('Error selecting plot:', error);
      }
    };

    (window as any).basicFSI = (gid: string) => {
      // Update button to loading state
      const button = document.getElementById(`basic-fsi-btn-${gid}`);
      if (button) {
        button.setAttribute('disabled', 'true');
        button.innerHTML = `
          <div class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <span>Loading...</span>
        `;
      }
      
      // Store the button reference for later reset
      (window as any).currentBasicFSIButton = button;
      
      handleBasicFSI(gid).catch(() => {
        // Only reset button if API call failed (modal won't show)
        if (button) {
          button.removeAttribute('disabled');
          button.innerHTML = `
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
            </svg>
            <span>Basic FSI</span>
          `;
        }
      });
    };

    (window as any).detailedFSI = (gid: string) => {
      // Update button to loading state
      const button = document.getElementById(`detailed-fsi-btn-${gid}`);
      if (button) {
        button.setAttribute('disabled', 'true');
        button.innerHTML = `
          <div class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <span>Loading...</span>
        `;
      }
      
      // Store the button reference for later reset
      (window as any).currentDetailedFSIButton = button;
      
      handleDetailedFSI(gid).catch(() => {
        // Only reset button if API call failed (modal won't show)
        if (button) {
          button.removeAttribute('disabled');
          button.innerHTML = `
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <span>Detailed FSI</span>
          `;
        }
      });
    };

    // Function to check if basic FSI is loading for a plot
    (window as any).isBasicFSILoading = (gid: string) => {
      return loadingBasicFSI.has(gid);
    };

    // Function to check if detailed FSI is loading for a plot
    (window as any).isDetailedFSILoading = (gid: string) => {
      return loadingDetailedFSI.has(gid);
    };

    // Function to open Custom FSI Report Wizard
    (window as any).customFSIReport = (gid: string) => {
      setCustomFSIGid(parseInt(gid));
      setShowCustomFSIWizard(true);
    };

    // Alias for Custom FSI Report Wizard (for popup button compatibility)
    (window as any).openCustomFSIWizard = (gid: string) => {
      setCustomFSIGid(parseInt(gid));
      setShowCustomFSIWizard(true);
    };

    // Function to reset fallback popup buttons
    (window as any).resetFSIButtons = () => {
      // Reset basic FSI button
      const basicButton = (window as any).currentBasicFSIButton;
      if (basicButton) {
        basicButton.removeAttribute('disabled');
        basicButton.innerHTML = `
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
          <span>Basic FSI</span>
        `;
        (window as any).currentBasicFSIButton = null;
      }

      // Reset detailed FSI button
      const detailedButton = (window as any).currentDetailedFSIButton;
      if (detailedButton) {
        detailedButton.removeAttribute('disabled');
        detailedButton.innerHTML = `
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <span>Detailed FSI</span>
        `;
        (window as any).currentDetailedFSIButton = null;
      }
    };

    return () => {
      delete (window as any).selectPlot;
      delete (window as any).basicFSI;
      delete (window as any).detailedFSI;
      delete (window as any).isBasicFSILoading;
      delete (window as any).isDetailedFSILoading;
      delete (window as any).customFSIReport;
      delete (window as any).openCustomFSIWizard;
      delete (window as any).resetFSIButtons;
      delete (window as any).currentBasicFSIButton;
      delete (window as any).currentDetailedFSIButton;
    };
  }, []);

  // Reset fallback popup buttons when modals are shown
  useEffect(() => {
    if (showFSIModal || showDetailedFSIModal) {
      // Reset buttons when any modal is shown
      const resetButtons = (window as any).resetFSIButtons;
      if (resetButtons) {
        resetButtons();
      }
    }
  }, [showFSIModal, showDetailedFSIModal]);

  // Initialize MapLibre GL JS map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    if (!mapboxgl.accessToken) {
      alert('Mapbox Access Token is missing. Please check your .env file and restart the server.');
      return;
    }

    // Add custom CSS for popups
    const style = document.createElement('style');
    style.textContent = `
      .mapboxgl-popup.custom-popup .mapboxgl-popup-content {
        padding: 0;
        border-radius: 8px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        border: none;
      }
      .mapboxgl-popup.custom-popup .mapboxgl-popup-tip {
        border-top-color: white;
      }
      .mapboxgl-popup.custom-popup .mapboxgl-popup-close-button {
        display: none;
      }
    `;
    document.head.appendChild(style);

    // Create Mapbox map instance
    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapStyle,
        center: [72.5797, 23.0225], // Ahmedabad coordinates
        zoom: 13,
        pitch: 0,
        bearing: 0,
        attributionControl: true,
        preserveDrawingBuffer: true
      });

      // Add error handler
      map.current.on('error', (e) => {
        console.error('Mapbox error:', e);
      });
    } catch (error) {
      console.error('Failed to initialize Mapbox:', error);
      alert('Failed to initialize map. Please check your Mapbox token and internet connection.');
      return;
    }

    // Add navigation controls with pitch and bearing
    map.current.addControl(new mapboxgl.NavigationControl({
      visualizePitch: true,
      showCompass: true
    }), 'top-right');

    // Wait for map to load, then add vector tiles
    map.current.on('load', () => {
      setIsMapLoaded(true);
      
      // Add a small delay to ensure everything is ready
      setTimeout(() => {
        addVectorTileLayer();
      }, 100);
    });

    // Cleanup on unmount
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Helper function to close current popup
  const closeCurrentPopup = () => {
    if (currentPopup.current) {
      currentPopup.current.remove();
      currentPopup.current = null;
    }
  };

  const changeMapStyle = (newStyle: string) => {
    if (!map.current) return;
    
    // Store current map state
    const center = map.current.getCenter();
    const zoom = map.current.getZoom();
    const bearing = map.current.getBearing();
    const pitch = map.current.getPitch();
    
    setMapStyle(newStyle);
    map.current.setStyle(newStyle);
    
    // Re-add custom layers after style loads
    map.current.once('style.load', () => {
      if (!map.current) return;
      
      // Restore map position
      map.current.setCenter(center);
      map.current.setZoom(zoom);
      map.current.setBearing(bearing);
      map.current.setPitch(pitch);
      
      // Re-add vector tile layers
      addVectorTileLayer();
    });
  };

  // Add vector tile layer with styling
  const addVectorTileLayer = () => {
    if (!map.current) return;

    try {
      // Get vector tile source configuration
      const vectorSource = tileService.getVectorTileSource();
      
      // Add vector tile source
      map.current.addSource('kshetram-tiles', vectorSource);

      // Find the base layer ID to insert custom layers above it
      let beforeLayer: string | undefined = undefined;
      const layers = map.current.getStyle().layers;
      
      // Add layers above the base OSM layer
      for (let i = layers.length - 1; i >= 0; i--) {
        if (layers[i].type === 'raster' || layers[i].id === 'osm') {
          beforeLayer = i + 1 < layers.length ? layers[i + 1].id : undefined;
          break;
        }
      }

      // TPS Boundaries - Fill (Visible at zoom 8-9, hidden when FPS plots appear)
      map.current.addLayer({
        id: 'tps-fill',
        type: 'fill',
        source: 'kshetram-tiles',
        'source-layer': 'road_network', // All data is in road_network layer
        filter: ['==', '_layer_type', 'tps_boundary'],
        paint: {
          'fill-color': 'rgba(255, 0, 0, 0.3)',
          'fill-outline-color': '#ff0000',
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 0.6,   // Visible at overview levels
            9, 0.4,   // Fading out
            10, 0     // Hidden when FPS plots appear
          ]
        },
        minzoom: 8,
        maxzoom: 10  // Hide when FPS plots become visible
      }, beforeLayer);

      // TPS Boundaries - Outline (Same visibility as fill)
      map.current.addLayer({
        id: 'tps-line',
        type: 'line',
        source: 'kshetram-tiles',
        'source-layer': 'road_network',
        filter: ['==', '_layer_type', 'tps_boundary'],
        paint: {
          'line-color': '#ff0000',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 2,   // Thinner at overview
            9, 3    // Medium thickness before hiding
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 0.8,
            9, 0.5,
            10, 0   // Fade out completely
          ]
        },
        minzoom: 8,
        maxzoom: 10  // Hide when FPS plots become visible
      }, beforeLayer);

      // Survey Plots - Fill (Color-coded by zone) - Render first so FPS appears on top
      map.current.addLayer({
        id: 'survey-fill',
        type: 'fill',
        source: 'kshetram-tiles',
        'source-layer': 'road_network',
        filter: ['==', '_layer_type', 'dp_survey'],
        paint: {
          'fill-color': getSurveyZoneColorExpression(),  // Zone-based colors for distinction
          'fill-outline-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            '#FFD700',  // Gold outline when selected
            '#27AE60'   // Green outline for distinction
          ],
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            12, 0.4,
            16, 0.6
          ]
        },
        minzoom: 12,
        maxzoom: 18
      }, beforeLayer);
      console.log('✓ Survey fill layer added');

      // Debug: Query features to see if survey data exists
      setTimeout(() => {
        const surveyFeatures = map.current?.querySourceFeatures('kshetram-tiles', {
          sourceLayer: 'road_network',
          filter: ['==', '_layer_type', 'dp_survey']
        }) || [];
        console.log(`Survey features found in tile source: ${surveyFeatures.length}`);
        if (surveyFeatures.length > 0) {
          console.log('Sample survey feature:', surveyFeatures[0]);
        } else {
          // Debug: Check what _layer_type values exist
          const allFeatures = map.current?.querySourceFeatures('kshetram-tiles', {
            sourceLayer: 'road_network'
          }) || [];
          const layerTypes = new Set(allFeatures.map(f => f.properties?._layer_type));
          console.log('Available _layer_type values in tiles:', Array.from(layerTypes));
          console.log('Total features in tiles:', allFeatures.length);
        }
      }, 1000);

      // Survey Plots - Outline
      map.current.addLayer({
        id: 'survey-line',
        type: 'line',
        source: 'kshetram-tiles',
        'source-layer': 'road_network',
        filter: ['==', '_layer_type', 'dp_survey'],
        paint: {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            '#FFD700',  // Gold when selected
            '#27AE60'   // Green outline
          ],
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            12, [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              3,
              1.5  // Thinner than FPS boundaries
            ],
            16, [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              5,
              2.5  // Thinner than FPS boundaries
            ]
          ],
          'line-opacity': 1
        },
        minzoom: 12,
        maxzoom: 18
      }, beforeLayer);
      console.log('✓ Survey line layer added');

      // Debug: Check survey features rendering
      const handleSurveyRender = () => {
        const surveyRendered = map.current?.queryRenderedFeatures({ layers: ['survey-fill'] }) || [];
        if (surveyRendered.length > 0) {
          console.log(`📊 Survey features rendering: ${surveyRendered.length} features visible on map`);
          // Remove this listener after first successful render to avoid console spam
          if (map.current) {
            map.current.off('render', handleSurveyRender);
          }
        }
      };
      map.current.on('render', handleSurveyRender);

      // Debug: Check survey features at different zoom levels
      const trackSurveyFeaturesByZoom = () => {
        const currentZoom = Math.round(map.current?.getZoom() || 0);
        const surveySource = map.current?.querySourceFeatures('kshetram-tiles', {
          sourceLayer: 'road_network',
          filter: ['==', '_layer_type', 'dp_survey']
        }) || [];
        if (currentZoom >= 12) {
          console.log(`🔍 Zoom ${currentZoom}: Survey features in tiles = ${surveySource.length}`);
        }
      };
      
      map.current.on('zoomend', trackSurveyFeaturesByZoom);
      trackSurveyFeaturesByZoom(); // Initial check

      // Debug: Check FPS features too
      setTimeout(() => {
        const fpsFeatures = map.current?.querySourceFeatures('kshetram-tiles', {
          sourceLayer: 'road_network',
          filter: ['==', '_layer_type', 'fps_plot']
        }) || [];
        console.log(`FPS features found: ${fpsFeatures.length}`);
        
        const tpsFeatures = map.current?.querySourceFeatures('kshetram-tiles', {
          sourceLayer: 'road_network',
          filter: ['==', '_layer_type', 'tps_boundary']
        }) || [];
        console.log(`TPS features found: ${tpsFeatures.length}`);
        
        const roadFeatures = map.current?.querySourceFeatures('kshetram-tiles', {
          sourceLayer: 'road_network',
          filter: ['==', '_layer_type', 'road_segment']
        }) || [];
        console.log(`Road features found: ${roadFeatures.length}`);
      }, 1000);

      // FPS Plots - Fill (Visible at zoom 10+) - Dynamic land use colors - Renders on top of Survey
      map.current.addLayer({
        id: 'fps-fill',
        type: 'fill',
        source: 'kshetram-tiles',
        'source-layer': 'road_network',
        filter: ['==', '_layer_type', 'fps_plot'],
        paint: {
          'fill-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            '#FFD700',  // Gold when selected
            getLandUseColorExpression()  // Dynamic colors based on reser_type/reser_use
          ],
          'fill-outline-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            '#FFD700',  // Gold outline when selected
            '#1E88E5'   // Blue outline for FPS plots (distinct from survey green)
          ],
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, 0.5,
            13, 0.6,
            16, 0.7
          ]
        },
        minzoom: 10,
        maxzoom: 18
      }, beforeLayer);

      // FPS Plots - Outline (Bold blue boundaries)
      map.current.addLayer({
        id: 'fps-line',
        type: 'line',
        source: 'kshetram-tiles',
        'source-layer': 'road_network',
        filter: ['==', '_layer_type', 'fps_plot'],
        paint: {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            '#FFD700',  // Gold when selected
            '#1E88E5'   // Blue outline for FPS plots
          ],
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              2,  // Selected at low zoom
              1   // Thin when far out for cleaner view
            ],
            13, [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              3,  // Selected at medium zoom
              2   // Medium thickness
            ],
            18, [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              6,  // Very thick when selected and zoomed in
              4   // Bold when zoomed in for clear boundaries
            ]
          ],
          'line-opacity': 0.9
        },
        minzoom: 10,
        maxzoom: 18
      }, beforeLayer);

      // Roads - Fill (Visible at zoom 13+)
      map.current.addLayer({
        id: 'roads-fill',
        type: 'fill',
        source: 'kshetram-tiles',
        'source-layer': 'road_network',
        filter: ['==', '_layer_type', 'road_segment'],
        paint: {
          'fill-color': 'rgba(0, 0, 255, 0.7)',
          'fill-outline-color': '#0000ff',
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            13, 0.4,  // Subtle when first appearing
            16, 0.7   // More prominent at high zoom
          ]
        },
        minzoom: 13,
        maxzoom: 18
      }, beforeLayer);

      // Roads - Outline
      map.current.addLayer({
        id: 'roads-line',
        type: 'line',
        source: 'kshetram-tiles',
        'source-layer': 'road_network',
        filter: ['==', '_layer_type', 'road_segment'],
        paint: {
          'line-color': '#0000ff',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            13, 2,  // Medium when first visible
            16, 4   // Thicker at high zoom
          ],
          'line-opacity': 0.8
        },
        minzoom: 13,
        maxzoom: 18
      }, beforeLayer);

      // Note: TPS Labels removed as they require glyphs configuration
      // and TPS boundaries are hidden when FPS plots are visible anyway

      // Unified click handler for both FPS and Survey plots
      const handleMapClick = async (e: mapboxgl.MapMouseEvent) => {
        // Query both FPS and Survey features at click point
        const fpsFeatures = map.current!.queryRenderedFeatures(e.point, { layers: ['fps-fill'] });
        const surveyFeatures = map.current!.queryRenderedFeatures(e.point, { layers: ['survey-fill'] });
        
        if (fpsFeatures.length === 0 && surveyFeatures.length === 0) return;
        
        closeCurrentPopup();
        
        const fpsFeature = fpsFeatures[0];
        const surveyFeature = surveyFeatures[0];
        
        let fpsProperties: any = {};
        let fpsParsedProperties: any = {};
        let fpsGid: string | null = null;
        
        let surveyProperties: any = {};
        let surveyParsedProperties: any = {};
        let surveyId: string | null = null;
        
        // Extract FPS data
        if (fpsFeature) {
          fpsProperties = fpsFeature.properties || {};
          fpsGid = fpsProperties?.gid ?? fpsProperties?.fps_code ?? null;
          
          if (!fpsGid && fpsProperties?.properties) {
            try {
              fpsParsedProperties = JSON.parse(fpsProperties.properties);
              fpsGid = fpsParsedProperties.gid || fpsParsedProperties.fps_code || null;
            } catch (e) {
              // Ignore malformed nested properties
            }
          }
          
          // Highlight FPS feature
          if (map.current && fpsFeature.id !== undefined) {
            if (selectedFeatureRef.current && selectedFeatureRef.current.id !== fpsFeature.id) {
              map.current.setFeatureState(
                {
                  source: selectedFeatureRef.current.source,
                  sourceLayer: selectedFeatureRef.current.sourceLayer,
                  id: selectedFeatureRef.current.id
                },
                { selected: false }
              );
            }

            map.current.setFeatureState(
              {
                source: 'kshetram-tiles',
                sourceLayer: 'road_network',
                id: fpsFeature.id
              },
              { selected: true }
            );

            selectedFeatureRef.current = {
              id: fpsFeature.id,
              source: 'kshetram-tiles',
              sourceLayer: 'road_network'
            };
          }
        }
        
        // Extract Survey data
        if (surveyFeature) {
          surveyProperties = surveyFeature.properties || {};
          surveyId = surveyProperties?.gid ?? surveyProperties?.survey_id ?? surveyProperties?.survey_no ?? null;
          
          if (!surveyId && surveyProperties?.properties) {
            try {
              surveyParsedProperties = JSON.parse(surveyProperties.properties);
              surveyId = surveyParsedProperties.gid || surveyParsedProperties.survey_id || surveyParsedProperties.survey_no || null;
            } catch (e) {
              // Ignore malformed nested properties
            }
          }
          
          // Highlight Survey feature if no FPS feature
          if (!fpsFeature && map.current && surveyFeature.id !== undefined) {
            if (selectedFeatureRef.current && selectedFeatureRef.current.id !== surveyFeature.id) {
              map.current.setFeatureState(
                {
                  source: selectedFeatureRef.current.source,
                  sourceLayer: selectedFeatureRef.current.sourceLayer,
                  id: selectedFeatureRef.current.id
                },
                { selected: false }
              );
            }

            map.current.setFeatureState(
              {
                source: 'kshetram-tiles',
                sourceLayer: 'road_network',
                id: surveyFeature.id
              },
              { selected: true }
            );

            selectedFeatureRef.current = {
              id: surveyFeature.id,
              source: 'kshetram-tiles',
              sourceLayer: 'road_network'
            };
          }
        }
        
        // Build unified popup
        const hasMultipleLayers = fpsFeature && surveyFeature;
        
        const unifiedPopup = `
          <div class="bg-white rounded-lg shadow-xl border-0 overflow-hidden" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 380px;">
            ${hasMultipleLayers ? `
              <div class="bg-gradient-to-r from-emerald-500 to-purple-600 text-white px-4 py-2">
                <h3 class="text-base font-bold">Combined Plot Details</h3>
                <p class="text-xs opacity-90">FPS + Survey Data</p>
              </div>
            ` : fpsFeature ? `
              <div class="bg-emerald-500 text-white px-4 py-2">
                <h3 class="text-base font-bold">FPS Plot ${fpsGid || ''}</h3>
              </div>
            ` : `
              <div class="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2">
                <h3 class="text-base font-bold">Survey Plot ${surveyId || ''}</h3>
              </div>
            `}
            
            <div class="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
              ${fpsFeature ? `
                <div class="pb-3 ${surveyFeature ? 'border-b border-gray-200' : ''}"> 
                  <div class="flex items-center gap-2 mb-2">
                    <div class="w-3 h-3 rounded-sm bg-emerald-500"></div>
                    <span class="text-xs font-bold text-emerald-700 uppercase tracking-wide">FPS Plot</span>
                  </div>
                  <div class="grid grid-cols-2 gap-2 text-sm">
                    ${fpsGid ? `
                      <div class="flex flex-col">
                        <span class="text-gray-500 text-xs font-medium">GID</span>
                        <span class="text-gray-900 font-semibold">${fpsGid}</span>
                      </div>
                    ` : ''}
                    <div class="flex flex-col">
                      <span class="text-gray-500 text-xs font-medium">FPS Code</span>
                      <span class="text-gray-900 font-semibold">${fpsProperties?.fps_code || 'N/A'}</span>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-gray-500 text-xs font-medium">Village</span>
                      <span class="text-gray-700">${fpsProperties?.village || fpsParsedProperties?.village || 'N/A'}</span>
                    </div>
                    ${fpsProperties?.district || fpsParsedProperties?.district ? `
                      <div class="flex flex-col">
                        <span class="text-gray-500 text-xs font-medium">District</span>
                        <span class="text-gray-700">${fpsProperties?.district || fpsParsedProperties?.district}</span>
                      </div>
                    ` : ''}
                    ${fpsProperties?.taluka || fpsParsedProperties?.taluka ? `
                      <div class="flex flex-col">
                        <span class="text-gray-500 text-xs font-medium">Taluka</span>
                        <span class="text-gray-700">${fpsProperties?.taluka || fpsParsedProperties?.taluka}</span>
                      </div>
                    ` : ''}
                    <div class="flex flex-col">
                      <span class="text-gray-500 text-xs font-medium">Plot No</span>
                      <span class="text-gray-700">${fpsParsedProperties?.fp_no || fpsProperties?.fp_no || fpsProperties?.plot_no || 'N/A'}</span>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-gray-500 text-xs font-medium">Area</span>
                      <span class="text-gray-700">${fpsParsedProperties?.fp_area_final ? Math.round(fpsParsedProperties.fp_area_final) + ' sq.m' : (fpsProperties?.area_sqmt_calculated ? Math.round(fpsProperties.area_sqmt_calculated) + ' sq.m' : (fpsProperties?.area || 'N/A'))}</span>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-gray-500 text-xs font-medium">TPS</span>
                      <span class="text-gray-700">${fpsParsedProperties?.tps_name || fpsProperties?.tps_name || 'N/A'}</span>
                    </div>
                    ${fpsProperties?.land_use || fpsParsedProperties?.land_use ? `
                      <div class="flex flex-col col-span-2">
                        <span class="text-gray-500 text-xs font-medium">Land Use</span>
                        <span class="text-gray-700">${fpsProperties?.land_use || fpsParsedProperties?.land_use}</span>
                      </div>
                    ` : ''}
                    ${fpsProperties?.zone || fpsParsedProperties?.zone ? `
                      <div class="flex flex-col">
                        <span class="text-gray-500 text-xs font-medium">Zone</span>
                        <span class="text-gray-700">${fpsProperties?.zone || fpsParsedProperties?.zone}</span>
                      </div>
                    ` : ''}
                    ${fpsProperties?.authority || fpsParsedProperties?.authority ? `
                      <div class="flex flex-col">
                        <span class="text-gray-500 text-xs font-medium">Authority</span>
                        <span class="text-gray-700">${fpsProperties?.authority || fpsParsedProperties?.authority}</span>
                      </div>
                    ` : ''}
                    ${fpsProperties?.tenure || fpsParsedProperties?.tenure ? `
                      <div class="flex flex-col col-span-2">
                        <span class="text-gray-500 text-xs font-medium">Tenure</span>
                        <span class="text-gray-700">${fpsProperties?.tenure || fpsParsedProperties?.tenure}</span>
                      </div>
                    ` : ''}
                  </div>
                </div>
              ` : ''}              ${surveyFeature ? `
                <div class="pb-3">
                  <div class="flex items-center gap-2 mb-2">
                    <div class="w-3 h-3 rounded-sm bg-purple-600"></div>
                    <span class="text-xs font-bold text-purple-700 uppercase tracking-wide">Survey Plot</span>
                  </div>
                  <div class="grid grid-cols-2 gap-2 text-sm">
                    <div class="flex flex-col">
                      <span class="text-gray-500 text-xs font-medium">Survey No</span>
                      <span class="text-gray-900 font-semibold">${surveyProperties?.survey_no || surveyId || 'N/A'}</span>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-gray-500 text-xs font-medium">Village</span>
                      <span class="text-gray-700">${surveyProperties?.village || surveyParsedProperties?.village || 'N/A'}</span>
                    </div>
                    ${surveyProperties?.district ? `
                      <div class="flex flex-col">
                        <span class="text-gray-500 text-xs font-medium">District</span>
                        <span class="text-gray-700">${surveyProperties.district}</span>
                      </div>
                    ` : ''}
                    ${surveyProperties?.taluka ? `
                      <div class="flex flex-col">
                        <span class="text-gray-500 text-xs font-medium">Taluka</span>
                        <span class="text-gray-700">${surveyProperties.taluka}</span>
                      </div>
                    ` : ''}
                    ${surveyProperties?.area_sqmt_calculated || surveyProperties?.area_sqmt_text ? `
                      <div class="flex flex-col">
                        <span class="text-gray-500 text-xs font-medium">Area</span>
                        <span class="text-gray-700">${surveyProperties?.area_sqmt_calculated ? Math.round(surveyProperties.area_sqmt_calculated) + ' sq.m' : surveyProperties?.area_sqmt_text}</span>
                      </div>
                    ` : ''}
                    ${surveyProperties?.zone ? `
                      <div class="flex flex-col">
                        <span class="text-gray-500 text-xs font-medium">Zone</span>
                        <span class="text-gray-700">${surveyProperties.zone}</span>
                      </div>
                    ` : ''}
                    ${surveyProperties?.land_use ? `
                      <div class="flex flex-col col-span-2">
                        <span class="text-gray-500 text-xs font-medium">Land Use</span>
                        <span class="text-gray-700">${surveyProperties.land_use}</span>
                      </div>
                    ` : ''}
                    ${surveyProperties?.tenure ? `
                      <div class="flex flex-col col-span-2">
                        <span class="text-gray-500 text-xs font-medium">Tenure</span>
                        <span class="text-gray-700">${surveyProperties.tenure}</span>
                      </div>
                    ` : ''}
                  </div>
                </div>
              ` : ''}
            </div>
            
            ${fpsGid ? `
              <div class="bg-gray-50 px-4 py-3 space-y-2 border-t border-gray-200">
                <button 
                  onclick="window.selectPlot('${fpsGid}')"
                  class="w-full bg-[#179246] hover:bg-[#147a3c] text-white text-sm py-2 rounded-lg transition-colors duration-200 font-medium"
                >
                  Select FPS Plot
                </button>
                <div class="grid grid-cols-2 gap-2">
                  <button 
                    onclick="window.basicFSI('${fpsGid}')"
                    id="basic-fsi-btn-${fpsGid}"
                    class="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm py-2 px-2 rounded-lg transition-colors duration-200 flex items-center justify-center gap-1"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                    </svg>
                    <span>Basic FSI</span>
                  </button>
                  <button 
                    onclick="window.detailedFSI('${fpsGid}')"
                    id="detailed-fsi-btn-${fpsGid}"
                    class="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm py-2 px-2 rounded-lg transition-colors duration-200 flex items-center justify-center gap-1"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    <span>Detailed FSI</span>
                  </button>
                  <button 
                    onclick="window.openCustomFSIWizard('${fpsGid}')"
                    id="custom-fsi-btn-${fpsGid}"
                    class="col-span-2 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white text-sm py-2 px-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium shadow-md"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                    </svg>
                    <span>Custom FSI Report</span>
                  </button>
                </div>
              </div>
            ` : ''}
          </div>
        `;
        
        currentPopup.current = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          maxWidth: '400px',
          className: 'custom-popup'
        })
          .setLngLat(e.lngLat)
          .setHTML(unifiedPopup)
          .addTo(map.current!);
      };

      // Add unified click handler for FPS plots
      map.current.on('click', 'fps-fill', handleMapClick);
      
      // Add unified click handler for Survey plots
      map.current.on('click', 'survey-fill', handleMapClick);

      // Keep old FPS handler for backward compatibility (remove duplicated code)
      map.current.on('click', 'fps-fill-old-handler', async (e) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const properties = feature.properties;
          
          // Close any existing popup
          closeCurrentPopup();
          
          // Get gid directly from flattened props; fallback to nested JSON if present
          let parsedProperties: any = {};
          let gid = properties?.gid ?? properties?.fps_code ?? null;
          
          if (!gid && properties?.properties) {
            try {
              parsedProperties = JSON.parse(properties.properties);
              gid = parsedProperties.gid || parsedProperties.fps_code || null;
            } catch (e) {
              // Ignore malformed nested properties
            }
          }
          


          if (gid) {
            try {
              const plot = await geospatialService.getFPSByGID(gid.toString());
              setSelectedPlot(plot);
              
              // Highlight selected plot and clear previous selection
              if (map.current && feature.id !== undefined) {
                if (selectedFeatureRef.current && selectedFeatureRef.current.id !== feature.id) {
                  map.current.setFeatureState(
                    {
                      source: selectedFeatureRef.current.source,
                      sourceLayer: selectedFeatureRef.current.sourceLayer,
                      id: selectedFeatureRef.current.id
                    },
                    { selected: false }
                  );
                }

                map.current.setFeatureState(
                  {
                    source: 'kshetram-tiles',
                    sourceLayer: 'road_network',
                    id: feature.id
                  },
                  { selected: true }
                );

                selectedFeatureRef.current = {
                  id: feature.id,
                  source: 'kshetram-tiles',
                  sourceLayer: 'road_network'
                };
              }

              // Generate popup HTML using the new component
              const popupContent = generatePopupHTML({
                gid: gid.toString(),
                properties,
                parsedProperties,
                loadingBasicFSI: loadingBasicFSI.has(gid.toString()),
                loadingDetailedFSI: loadingDetailedFSI.has(gid.toString())
              });
              
              currentPopup.current = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                maxWidth: '320px',
                className: 'custom-popup'
              })
                .setLngLat(e.lngLat)
                .setHTML(popupContent)
                .addTo(map.current!);
                
            } catch (error) {
              console.error('Error fetching plot details:', error);
              
              // Create popup with available data even if API fails
              const fallbackPopup = `
                <div class="bg-white rounded-lg shadow-lg border-0 overflow-hidden" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
                  <div class="bg-emerald-500 text-white px-4 py-2">
                    <h3 class="font-semibold">Plot ${gid}</h3>
                  </div>
                  <div class="p-4 space-y-2 text-sm">
                    <div><span class="text-gray-500">Code:</span> <span class="font-medium">${properties?.fps_code || 'N/A'}</span></div>
                    <div><span class="text-gray-500">Village:</span> <span class="font-medium">${properties?.village || parsedProperties?.village || 'N/A'}</span></div>
                    <div><span class="text-gray-500">Plot No:</span> <span class="font-medium">${parsedProperties?.fp_no || 'N/A'}</span></div>
                    <div><span class="text-gray-500">Area:</span> <span class="font-medium">${parsedProperties?.fp_area_final ? Math.round(parsedProperties.fp_area_final) + ' sq.m' : 'N/A'}</span></div>
                    <div><span class="text-gray-500">TPS:</span> <span class="font-medium">${parsedProperties?.tps_name || 'N/A'}</span></div>
                  </div>
                  <div class="bg-gray-50 px-4 py-2 space-y-2">
                    <button 
                      onclick="window.selectPlot('${gid}')"
                      class="w-full bg-[#179246] hover:bg-[#147a3c] text-white text-sm py-2 rounded transition-colors duration-200"
                    >
                      Select Plot
                    </button>
                    <div class="flex gap-2">
                      <button 
                        onclick="window.basicFSI('${gid}')"
                        id="basic-fsi-btn-${gid}"
                        class="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white text-sm py-2 px-3 rounded transition-colors duration-200 flex items-center justify-center gap-1"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                        </svg>
                        <span>Basic FSI</span>
                      </button>
                      <button 
                        onclick="window.detailedFSI('${gid}')"
                        id="detailed-fsi-btn-${gid}"
                        class="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed text-white text-sm py-2 px-3 rounded transition-colors duration-200 flex items-center justify-center gap-1"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        <span>Detailed FSI</span>
                      </button>
                    </div>
                  </div>
                </div>
              `;
              
              currentPopup.current = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                maxWidth: '320px',
                className: 'custom-popup'
              })
                .setLngLat(e.lngLat)
                .setHTML(fallbackPopup)
                .addTo(map.current!);
            }
          } else {
            // Show basic popup without GID
            const basicPopup = `
              <div class="bg-white rounded-lg shadow-lg border-0 overflow-hidden" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
                <div class="bg-emerald-500 text-white px-4 py-2">
                  <h3 class="font-semibold">FPS Plot</h3>
                </div>
                <div class="p-4 space-y-2 text-sm">
                  <div><span class="text-gray-500">Code:</span> <span class="font-medium">${properties?.fps_code || 'N/A'}</span></div>
                  <div><span class="text-gray-500">Village:</span> <span class="font-medium">${properties?.village || 'N/A'}</span></div>
                  <div class="text-xs text-orange-600 mt-2">No GID available for this plot</div>
                </div>
              </div>
            `;
            
            currentPopup.current = new mapboxgl.Popup({
              closeButton: false,
              closeOnClick: false,
              maxWidth: '280px',
              className: 'custom-popup'
            })
              .setLngLat(e.lngLat)
              .setHTML(basicPopup)
              .addTo(map.current!);
          }
        }
      });

      // Add click handlers for TPS boundaries
      map.current.on('click', 'tps-fill', (e) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const properties = feature.properties;
          
              // Parse properties JSON if available
              let parsedProperties: any = {};
              try {
                if (properties && properties.properties) {
                  parsedProperties = JSON.parse(properties.properties);
                }
              } catch (e) {
                // Ignore malformed nested properties
              }          const popupContent = `
            <div class="p-4 min-w-64">
              <h3 class="font-bold text-lg text-red-600 mb-3 border-b pb-2">TPS Boundary</h3>
              
              <div class="grid grid-cols-2 gap-2 text-sm">
                <div><strong>Name:</strong></div>
                <div>${properties?.name || properties?.tp_name || 'N/A'}</div>
                
                <div><strong>TPS No:</strong></div>
                <div>${properties?.tps_no || 'N/A'}</div>
                
                <div><strong>Village:</strong></div>
                <div>${properties?.village || 'N/A'}</div>
                
                <div><strong>City:</strong></div>
                <div>${properties?.city || 'N/A'}</div>
                
                <div><strong>Authority:</strong></div>
                <div>${properties?.authority || 'N/A'}</div>
                
                <div><strong>Status:</strong></div>
                <div class="px-2 py-1 rounded text-xs ${properties?.status === 'Final' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}">${properties?.status || 'N/A'}</div>
              </div>
              
              ${Object.keys(parsedProperties).length > 0 ? `
                <div class="mt-3 pt-2 border-t border-gray-200">
                  <div class="text-xs text-gray-600">
                    <div><strong>TPS ID:</strong> ${parsedProperties.tps_id || 'N/A'}</div>
                    <div><strong>Show/Hide:</strong> ${parsedProperties.show_hide ? 'Visible' : 'Hidden'}</div>
                  </div>
                </div>
              ` : ''}
            </div>
          `;
          
          new mapboxgl.Popup({
            closeButton: true,
            closeOnClick: false
          })
            .setLngLat(e.lngLat)
            .setHTML(popupContent)
            .addTo(map.current!);
        }
      });

      // Add click handlers for road segments
      map.current.on('click', 'roads-fill', (e) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const properties = feature.properties;

          // Close any existing popup
          closeCurrentPopup();

          const popupContent = `
            <div class="bg-white rounded-lg shadow-lg border-0 overflow-hidden" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <div class="bg-blue-500 text-white px-4 py-2">
                <h3 class="font-semibold text-sm">Road Segment</h3>
              </div>
              <div class="p-3 space-y-2 text-sm">
                <div><span class="text-gray-500">Name:</span> <span class="font-medium">${properties?.name || 'N/A'}</span></div>
                <div><span class="text-gray-500">Type:</span> <span class="font-medium">${properties?.road_type || 'N/A'}</span></div>
                <div><span class="text-gray-500">Village:</span> <span class="font-medium">${properties?.village || 'N/A'}</span></div>
                <div><span class="text-gray-500">Authority:</span> <span class="font-medium">${properties?.authority || 'N/A'}</span></div>
              </div>
            </div>
          `;
          
          currentPopup.current = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            maxWidth: '250px',
            className: 'custom-popup'
          })
            .setLngLat(e.lngLat)
            .setHTML(popupContent)
            .addTo(map.current!);
        }
      });

      // Close popup when clicking on empty areas
      map.current.on('click', (e) => {
        // Check if click was on a feature
        const features = map.current!.queryRenderedFeatures(e.point, {
          layers: ['fps-fill', 'roads-fill', 'tps-fill', 'survey-fill']
        });
        
        // If no features were clicked, close the popup
        if (features.length === 0) {
          closeCurrentPopup();
          if (selectedFeatureRef.current) {
            map.current!.setFeatureState(
              {
                source: selectedFeatureRef.current.source,
                sourceLayer: selectedFeatureRef.current.sourceLayer,
                id: selectedFeatureRef.current.id
              },
              { selected: false }
            );
            selectedFeatureRef.current = null;
          }
        }
      });

      // Add hover effects
      map.current.on('mouseenter', 'fps-fill', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = 'pointer';
        }
      });

      map.current.on('mouseleave', 'fps-fill', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = '';
        }
      });

      map.current.on('mouseenter', 'survey-fill', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = 'pointer';
        }
      });

      map.current.on('mouseleave', 'survey-fill', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = '';
        }
      });

      map.current.on('mouseenter', 'tps-fill', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = 'pointer';
        }
      });

      map.current.on('mouseleave', 'tps-fill', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = '';
        }
      });

      map.current.on('mouseenter', 'roads-fill', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = 'pointer';
        }
      });

      map.current.on('mouseleave', 'roads-fill', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = '';
        }
      });

      // Add Mapbox 3D Buildings Layer
      map.current.addLayer({
        'id': '3d-buildings',
        'source': 'composite',
        'source-layer': 'building',
        'filter': ['==', 'extrude', 'true'],
        'type': 'fill-extrusion',
        'minzoom': 15,
        'paint': {
          'fill-extrusion-color': '#aaa',
          'fill-extrusion-height': [
            'interpolate', ['linear'], ['zoom'],
            15, 0,
            15.05, ['get', 'height']
          ],
          'fill-extrusion-base': [
            'interpolate', ['linear'], ['zoom'],
            15, 0,
            15.05, ['get', 'min_height']
          ],
          'fill-extrusion-opacity': 0.6
        }
      });
      
      // Apply land use colors to FPS plots based on their properties
      // Don't apply dynamic colors - let the style handle it with default color
      // This ensures smooth map performance
    } catch (error) {
      console.error('Error adding vector tile layer:', error);
    }
  };

  // Handle layer visibility changes
  useEffect(() => {
    if (!map.current || !isMapLoaded) return;

    // TPS Boundaries
    const tpsVisibility = layerVisibility.tpsBoundaries ? 'visible' : 'none';
    if (map.current.getLayer('tps-fill')) {
      map.current.setLayoutProperty('tps-fill', 'visibility', tpsVisibility);
    }
    if (map.current.getLayer('tps-line')) {
      map.current.setLayoutProperty('tps-line', 'visibility', tpsVisibility);
    }

    // FPS Plots
    const fpsVisibility = layerVisibility.fpsPlots ? 'visible' : 'none';
    if (map.current.getLayer('fps-fill')) {
      map.current.setLayoutProperty('fps-fill', 'visibility', fpsVisibility);
    }
    if (map.current.getLayer('fps-line')) {
      map.current.setLayoutProperty('fps-line', 'visibility', fpsVisibility);
    }

    // Survey Plots
    const surveyVisibility = layerVisibility.surveyPlots ? 'visible' : 'none';
    if (map.current.getLayer('survey-fill')) {
      map.current.setLayoutProperty('survey-fill', 'visibility', surveyVisibility);
      console.log('Survey-fill visibility set to:', surveyVisibility);
    } else {
      console.warn('survey-fill layer not found');
    }
    if (map.current.getLayer('survey-line')) {
      map.current.setLayoutProperty('survey-line', 'visibility', surveyVisibility);
      console.log('Survey-line visibility set to:', surveyVisibility);
    } else {
      console.warn('survey-line layer not found');
    }

    // Roads
    const roadsVisibility = layerVisibility.roads ? 'visible' : 'none';
    if (map.current.getLayer('roads-fill')) {
      map.current.setLayoutProperty('roads-fill', 'visibility', roadsVisibility);
    }
    if (map.current.getLayer('roads-line')) {
      map.current.setLayoutProperty('roads-line', 'visibility', roadsVisibility);
    }

    // 3D Buildings
    const buildings3DVisibility = layerVisibility.buildings3D ? 'visible' : 'none';
    if (map.current.getLayer('3d-buildings')) {
      map.current.setLayoutProperty('3d-buildings', 'visibility', buildings3DVisibility);
    }
  }, [layerVisibility, isMapLoaded]);

  // Toggle layer visibility
  const toggleLayer = (layerName: keyof typeof layerVisibility) => {
    setLayerVisibility(prev => ({
      ...prev,
      [layerName]: !prev[layerName]
    }));
  };

  // Toggle 3D view
  const toggle3DView = () => {
    if (!map.current) return;
    
    const newIs3D = !is3DView;
    setIs3DView(newIs3D);
    
    if (newIs3D) {
      // Enable 3D view with smooth animation
      const currentZoom = map.current.getZoom();
      
      // If zoom level is less than 15, zoom to 15 to show buildings
      if (currentZoom < 15) {
        map.current.easeTo({
          zoom: 15.5,
          pitch: 60,
          bearing: -20,
          duration: 1500
        });
      } else {
        map.current.easeTo({
          pitch: 60,
          bearing: -20,
          duration: 1000
        });
      }
    } else {
      // Return to 2D view
      map.current.easeTo({
        pitch: 0,
        bearing: 0,
        duration: 1000
      });
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !map.current) return;
    
    setIsSearching(true);
    closeCurrentPopup();
    
    try {
      // Check if it's a numeric GID search
      const isGidSearch = /^\d+$/.test(searchQuery.trim());
      
      if (isGidSearch) {
        // Search by GID
        const gid = searchQuery.trim();
        try {
          const plot = await geospatialService.getFPSByGID(gid);
          if (plot) {
            setSelectedPlot(plot);
            
            // Try to find and highlight the plot on the map
            const features = map.current.querySourceFeatures('kshetram-tiles', {
              sourceLayer: 'road_network',
              filter: ['==', '_layer_type', 'fps_plot']
            });
            console.log('FPS features found:', features.length);
            
            // Look for the plot with matching GID in properties
            let targetFeature = null;
            for (const feature of features) {
              try {
                if (feature.properties?.properties) {
                  const parsed = JSON.parse(feature.properties.properties);
                  if (parsed.gid && parsed.gid.toString() === gid) {
                    targetFeature = feature;
                    break;
                  }
                }
              } catch (e) {
                // Skip invalid JSON
              }
            }
            
            if (targetFeature && targetFeature.geometry?.type === 'Polygon') {
              // Calculate bounds of the polygon
              const coordinates = targetFeature.geometry.coordinates[0];
              let minLng = coordinates[0][0], maxLng = coordinates[0][0];
              let minLat = coordinates[0][1], maxLat = coordinates[0][1];
              
              coordinates.forEach(([lng, lat]) => {
                minLng = Math.min(minLng, lng);
                maxLng = Math.max(maxLng, lng);
                minLat = Math.min(minLat, lat);
                maxLat = Math.max(maxLat, lat);
              });
              
              // Center map on the plot
              map.current.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
                padding: 100,
                maxZoom: 16
              });
              
              // Highlight the feature
              if (targetFeature.id) {
                map.current.setFeatureState({
                  source: 'kshetram-tiles',
                  sourceLayer: 'road_network',
                  id: targetFeature.id
                }, { selected: true });
              }
              
              alert(`Found plot GID: ${gid}. Map centered on the plot.`);
            } else {
              alert(`Plot GID: ${gid} found in database but not visible on current map view. Try zooming to level 10+ and searching again.`);
            }
          } else {
            alert(`No plot found with GID: ${gid}`);
          }
        } catch (error) {
          console.error('Error searching by GID:', error);
          alert(`Error searching for GID: ${gid}`);
        }
      } else {
        // Search by TPS name
        const tpsName = searchQuery.trim().toLowerCase();
        const features = map.current.querySourceFeatures('kshetram-tiles', {
          sourceLayer: 'road_network',
          filter: ['==', '_layer_type', 'tps_boundary']
        });
        
        let foundFeatures = [];
        for (const feature of features) {
          try {
            if (feature.properties?.properties) {
              const parsed = JSON.parse(feature.properties.properties);
              if (parsed.tps_name && parsed.tps_name.toLowerCase().includes(tpsName)) {
                foundFeatures.push(feature);
              }
            }
          } catch (e) {
            // Skip invalid JSON
          }
        }
        
        if (foundFeatures.length > 0) {
          // Use the first match
          const targetFeature = foundFeatures[0];
          
          if (targetFeature.geometry?.type === 'Polygon') {
            // Calculate bounds of the TPS boundary
            const coordinates = targetFeature.geometry.coordinates[0];
            let minLng = coordinates[0][0], maxLng = coordinates[0][0];
            let minLat = coordinates[0][1], maxLat = coordinates[0][1];
            
            coordinates.forEach(([lng, lat]) => {
              minLng = Math.min(minLng, lng);
              maxLng = Math.max(maxLng, lng);
              minLat = Math.min(minLat, lat);
              maxLat = Math.max(maxLat, lat);
            });
            
            // Center map on the TPS
            map.current.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
              padding: 50,
              maxZoom: 14
            });
            
            if (targetFeature.properties && targetFeature.properties.properties) {
              const parsed = JSON.parse(targetFeature.properties.properties);
              alert(`Found TPS: ${parsed.tps_name}. Map centered on the boundary. ${foundFeatures.length > 1 ? `(${foundFeatures.length - 1} more matches found)` : ''}`);
            } else {
              alert(`Found TPS boundary. Map centered on the boundary. ${foundFeatures.length > 1 ? `(${foundFeatures.length - 1} more matches found)` : ''}`);
            }
          }
        } else {
          alert(`No TPS found matching: "${searchQuery}"`);
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      alert('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  // FSI Analysis Functions
  const handleBasicFSI = async (gid: string) => {
    // Prevent duplicate API calls
    if (loadingBasicFSI.has(gid)) {
      return;
    }

    setLoadingBasicFSI(prev => new Set(prev).add(gid));
    setIsLoadingFSI(true);
    setFsiError(null);
    
    try {
      const result = await fsiService.getBasicFSI(gid);
      setFsiData(result);
      setShowFSIModal(true);
    } catch (error) {
      console.error('FSI Analysis Error:', error);
      setFsiError('Failed to fetch FSI analysis. Please try again.');
    } finally {
      setLoadingBasicFSI(prev => {
        const newSet = new Set(prev);
        newSet.delete(gid);
        return newSet;
      });
      setIsLoadingFSI(false);
    }
  };

  const handleDetailedFSI = async (gid: string) => {
    // Prevent duplicate API calls
    if (loadingDetailedFSI.has(gid)) {
      return;
    }

    setLoadingDetailedFSI(prev => new Set(prev).add(gid));
    setIsLoadingDetailedFSI(true);
    setDetailedFsiError(null);
    
    try {
      const result = await fsiService.getDetailedFSIAnalysis(gid);
      setDetailedFsiData(result);
      setShowDetailedFSIModal(true);
    } catch (error) {
      console.error('Detailed FSI Analysis Error:', error);
      setDetailedFsiError('Failed to fetch detailed FSI analysis. Please try again.');
    } finally {
      setLoadingDetailedFSI(prev => {
        const newSet = new Set(prev);
        newSet.delete(gid);
        return newSet;
      });
      setIsLoadingDetailedFSI(false);
    }
  };

  const closeDetailedFSIModal = () => {
    setShowDetailedFSIModal(false);
    setDetailedFsiData(null);
    setDetailedFsiError(null);
    
    // Clean up spatial map if it exists
    if (spatialMap.current) {
      spatialMap.current.remove();
      spatialMap.current = null;
      setSpatialMapLoaded(false);
    }
  };

  const initializeSpatialMap = () => {
    if (!spatialMapContainer.current || spatialMap.current || !detailedFsiData) return;

    try {
      // Initialize the spatial analysis map with Mapbox
      const newSpatialMap = new mapboxgl.Map({
        container: spatialMapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [72.5714, 23.0225], // Default to Ahmedabad
        zoom: 12,
        maxZoom: 18,
        minZoom: 8
      });

      newSpatialMap.addControl(new mapboxgl.NavigationControl(), 'top-right');

      newSpatialMap.on('load', () => {
        setSpatialMapLoaded(true);
        
        // Add plot polygon if available
        if (detailedFsiData.polygon_data?.plot_polygon?.geometry) {
          try {
            const plotGeometry = detailedFsiData.polygon_data.plot_polygon.geometry;
            
            // Add plot boundary source
            newSpatialMap.addSource('plot-boundary', {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: plotGeometry as any,
                properties: detailedFsiData.polygon_data.plot_polygon.properties || {}
              }
            });

            // Add plot boundary fill
            newSpatialMap.addLayer({
              id: 'plot-fill',
              type: 'fill',
              source: 'plot-boundary',
              paint: {
                'fill-color': '#3B82F6',
                'fill-opacity': 0.3
              }
            });

            // Add plot boundary outline
            newSpatialMap.addLayer({
              id: 'plot-outline',
              type: 'line',
              source: 'plot-boundary',
              paint: {
                'line-color': '#1D4ED8',
                'line-width': 3
              }
            });

            // Calculate bounds and fit map
            if (plotGeometry.type === 'MultiPolygon' && plotGeometry.coordinates?.[0]?.[0]) {
              const coordinates = plotGeometry.coordinates[0][0];
              const lngs = coordinates.map((coord: number[]) => coord[0]);
              const lats = coordinates.map((coord: number[]) => coord[1]);
              
              const bounds = [
                [Math.min(...lngs), Math.min(...lats)], // Southwest corner
                [Math.max(...lngs), Math.max(...lats)]  // Northeast corner
              ];
              
              newSpatialMap.fitBounds(bounds as any, { padding: 50 });
            }
          } catch (error) {
            console.error('Error adding plot geometry:', error);
          }
        }

        // Add adjacent roads if available
        if (detailedFsiData.polygon_data?.adjacent_roads) {
          detailedFsiData.polygon_data.adjacent_roads.forEach((road, index) => {
            try {
              newSpatialMap.addSource(`road-${index}`, {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  geometry: road.geometry as any,
                  properties: road.properties || {}
                }
              });

              newSpatialMap.addLayer({
                id: `road-${index}`,
                type: 'line',
                source: `road-${index}`,
                paint: {
                  'line-color': '#EF4444',
                  'line-width': 4
                }
              });
            } catch (error) {
              console.error(`Error adding road ${index}:`, error);
            }
          });
        }

        // Add buffer zone if available
        if (detailedFsiData.polygon_data?.visualization_buffer?.geometry) {
          try {
            newSpatialMap.addSource('buffer-zone', {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: detailedFsiData.polygon_data.visualization_buffer.geometry as any,
                properties: detailedFsiData.polygon_data.visualization_buffer.properties || {}
              }
            });

            newSpatialMap.addLayer({
              id: 'buffer-outline',
              type: 'line',
              source: 'buffer-zone',
              paint: {
                'line-color': '#10B981',
                'line-width': 2,
                'line-dasharray': [5, 5]
              }
            });
          } catch (error) {
            console.error('Error adding buffer zone:', error);
          }
        }
      });

      spatialMap.current = newSpatialMap;
    } catch (error) {
      console.error('Error initializing spatial map:', error);
    }
  };

  // Initialize spatial map when tab becomes active and data is available
  useEffect(() => {
    if (activeDetailedTab === 'spatial-viz' && detailedFsiData && !spatialMap.current) {
      // Small delay to ensure container is rendered
      setTimeout(() => {
        initializeSpatialMap();
      }, 100);
    }
  }, [activeDetailedTab, detailedFsiData]);

  const closeFSIModal = () => {
    setShowFSIModal(false);
    setFsiData(null);
    setFsiError(null);
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-gray-50 font-sans selection:bg-[#179246] selection:text-white">
      {/* Map Container */}
      <div ref={mapContainer} className="absolute inset-0 z-0" />

      {/* Top Navigation Bar (Glassmorphism) */}
      <div className="absolute top-0 left-0 right-0 z-20 px-4 py-4 pointer-events-none">
        <div className="flex items-center justify-between max-w-[1920px] mx-auto">
          {/* Left: Brand & Search */}
          <div className="flex items-center gap-4 pointer-events-auto">
            {/* Logo Card */}
            <div className="bg-white/90 backdrop-blur-md shadow-lg rounded-2xl p-3 flex items-center gap-3 border border-white/20 transition-transform hover:scale-[1.02]">
              <img src="/kshetram-logo.svg" alt="Kshetram" className="h-8 w-auto" />
              <div className="hidden md:block pr-2">
                <h1 className="text-lg font-bold text-[#179246] leading-none tracking-tight">Kshetram</h1>
                <p className="text-[10px] text-gray-500 font-semibold tracking-widest uppercase mt-0.5">FSI Analysis</p>
              </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white/90 backdrop-blur-md shadow-lg rounded-2xl flex items-center p-1.5 border border-white/20 w-[320px] transition-all duration-300 focus-within:w-[420px] focus-within:ring-2 focus-within:ring-[#179246]/20 group">
              <Search className="h-5 w-5 text-gray-400 ml-3 group-focus-within:text-[#179246] transition-colors" />
              <input 
                type="text"
                placeholder="Search by GID or TPS name..."
                className="bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-400 w-full px-3 py-2"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                disabled={isSearching}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 mr-1"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <button 
                onClick={handleSearch}
                disabled={isSearching || !searchQuery.trim()}
                className="bg-[#179246] hover:bg-[#147a3c] disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl px-4 py-2 text-sm font-medium transition-all shadow-sm hover:shadow-md active:scale-95"
              >
                {isSearching ? '...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Right: User Profile & Actions */}
          <div className="flex items-center gap-3 pointer-events-auto">
            <div className="bg-white/90 backdrop-blur-md shadow-lg rounded-2xl p-1.5 border border-white/20 flex items-center gap-3 pl-2">
              <div className="flex flex-col items-end mr-1 hidden sm:flex">
                <span className="text-xs font-bold text-gray-700">Admin User</span>
                <span className="text-[10px] text-gray-500">Ahmedabad Urban Area</span>
              </div>
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#179246] to-[#58BA59] flex items-center justify-center text-white font-bold shadow-sm text-sm">
                AU
              </div>
              <div className="h-8 w-[1px] bg-gray-200 mx-1"></div>
              <button 
                onClick={handleLogout}
                className="p-2.5 hover:bg-red-50 text-gray-500 hover:text-red-500 rounded-xl transition-all duration-200 group"
                title="Logout"
              >
                <LogOut className="h-5 w-5 group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Left Sidebar: Layer Control (Floating) */}
      <div className="absolute top-28 left-4 z-10 flex flex-col gap-4 pointer-events-auto">
        <div className={`bg-white/90 backdrop-blur-md shadow-lg rounded-2xl border border-white/20 overflow-hidden transition-all duration-300 ${showLayers ? 'w-72' : 'w-14 h-14 rounded-full'}`}>
          <div 
            className={`flex items-center cursor-pointer hover:bg-gray-50/50 transition-colors ${showLayers ? 'p-4 justify-between' : 'w-full h-full justify-center'}`}
            onClick={() => setShowLayers(!showLayers)}
            title={showLayers ? "Collapse Layers" : "Show Layers"}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 bg-[#179246]/10 rounded-lg text-[#179246] ${!showLayers && 'p-0 bg-transparent'}`}>
                <Layers className="h-5 w-5" />
              </div>
              {showLayers && <span className="font-semibold text-gray-700">Map Layers</span>}
            </div>
            {showLayers && <ChevronDown className="h-4 w-4 text-gray-400" />}
          </div>
          
          {showLayers && (
            <div className="px-4 pb-4 space-y-1 animate-in slide-in-from-top-2 duration-200 max-h-[60vh] overflow-y-auto custom-scrollbar">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 mt-2">Base Maps</div>
              
              {/* Roads */}
              <label className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2.5 rounded-xl transition-colors group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center border border-blue-100 group-hover:border-blue-200 transition-colors">
                    <MapIcon className="h-4 w-4 text-blue-500" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">Road Network</span>
                </div>
                <div className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={layerVisibility.roads} onChange={() => toggleLayer('roads')} className="sr-only peer" />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#179246]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#179246]"></div>
                </div>
              </label>

              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 mt-4">Planning Layers</div>

              {/* TPS Boundaries */}
              <label className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2.5 rounded-xl transition-colors group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center border border-red-100 group-hover:border-red-200 transition-colors">
                    <div className="w-4 h-4 border-2 border-red-500 rounded-sm"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-700">TPS Boundaries</span>
                </div>
                <div className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={layerVisibility.tpsBoundaries} onChange={() => toggleLayer('tpsBoundaries')} className="sr-only peer" />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#179246]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#179246]"></div>
                </div>
              </label>

              {/* FPS Plots */}
              <label className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2.5 rounded-xl transition-colors group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center border border-green-100 group-hover:border-green-200 transition-colors">
                    <div className="w-4 h-4 bg-green-500/20 border border-green-600 rounded-sm"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-700">FPS Plots</span>
                </div>
                <div className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={layerVisibility.fpsPlots} onChange={() => toggleLayer('fpsPlots')} className="sr-only peer" />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#179246]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#179246]"></div>
                </div>
              </label>

              {/* Survey Plots */}
              <label className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2.5 rounded-xl transition-colors group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center border border-purple-100 group-hover:border-purple-200 transition-colors">
                    <div className="w-4 h-4 bg-purple-500/20 border border-purple-600 rounded-sm"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Survey Plots</span>
                </div>
                <div className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={layerVisibility.surveyPlots} onChange={() => toggleLayer('surveyPlots')} className="sr-only peer" />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#179246]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#179246]"></div>
                </div>
              </label>

              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 mt-4">Visualization</div>

              {/* 3D Buildings */}
              <label className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2.5 rounded-xl transition-colors group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center border border-orange-100 group-hover:border-orange-200 transition-colors">
                    <Box className="h-4 w-4 text-orange-500" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700">3D Buildings</span>
                    <span className="text-[10px] text-gray-400">Visible at zoom 15+</span>
                  </div>
                </div>
                <div className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={layerVisibility.buildings3D} onChange={() => toggleLayer('buildings3D')} className="sr-only peer" />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#179246]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#179246]"></div>
                </div>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Right Bottom: Map Controls */}
      <div className="absolute bottom-8 right-6 z-10 flex flex-col gap-3 pointer-events-auto">
        {/* 3D Toggle */}
        <button
          onClick={toggle3DView}
          className={`bg-white/90 backdrop-blur-md shadow-lg rounded-2xl p-3 border border-white/20 transition-all group flex items-center gap-2 ${is3DView ? 'bg-[#179246]/90 text-white' : 'hover:bg-white text-gray-700 hover:text-[#179246]'}`}
          title={is3DView ? 'Switch to 2D View' : 'Switch to 3D View'}
        >
          <Compass className={`h-6 w-6 transition-transform duration-500 ${is3DView ? 'rotate-45' : ''}`} />
          {is3DView && <span className="text-sm font-bold pr-1">3D</span>}
        </button>
      </div>

      {/* Top Right: Map Style Selector */}
      <div className="absolute top-24 right-4 z-10 pointer-events-auto">
        <div className="relative">
          <button
            onClick={() => setShowStyleSelector(!showStyleSelector)}
            className="bg-white/90 backdrop-blur-md hover:bg-white px-4 py-3 rounded-2xl shadow-lg transition-all duration-200 flex items-center gap-2 border border-gray-200"
            title="Change Map Style"
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-semibold text-gray-700">Map Style</span>
            <svg className={`w-4 h-4 text-gray-500 transition-transform ${showStyleSelector ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showStyleSelector && (
            <div className="absolute top-full mt-2 right-0 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200 overflow-hidden min-w-[240px]">
              <div className="p-2 space-y-1">
                <button
                  onClick={() => { changeMapStyle('mapbox://styles/mapbox/streets-v12'); setShowStyleSelector(false); }}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all ${mapStyle === 'mapbox://styles/mapbox/streets-v12' ? 'bg-green-50 text-green-700 font-semibold' : 'hover:bg-gray-100 text-gray-700'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium">Streets</div>
                      <div className="text-xs text-gray-500">Default view</div>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => { changeMapStyle('mapbox://styles/mapbox/satellite-streets-v12'); setShowStyleSelector(false); }}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all ${mapStyle === 'mapbox://styles/mapbox/satellite-streets-v12' ? 'bg-green-50 text-green-700 font-semibold' : 'hover:bg-gray-100 text-gray-700'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium">Satellite</div>
                      <div className="text-xs text-gray-500">Aerial imagery</div>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => { changeMapStyle('mapbox://styles/mapbox/outdoors-v12'); setShowStyleSelector(false); }}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all ${mapStyle === 'mapbox://styles/mapbox/outdoors-v12' ? 'bg-green-50 text-green-700 font-semibold' : 'hover:bg-gray-100 text-gray-700'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium">Outdoors</div>
                      <div className="text-xs text-gray-500">Terrain & trails</div>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => { changeMapStyle('mapbox://styles/mapbox/dark-v11'); setShowStyleSelector(false); }}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all ${mapStyle === 'mapbox://styles/mapbox/dark-v11' ? 'bg-green-50 text-green-700 font-semibold' : 'hover:bg-gray-100 text-gray-700'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium">Dark</div>
                      <div className="text-xs text-gray-500">Night mode</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Left: Legend */}
      <div className="absolute bottom-8 left-4 z-10 pointer-events-auto">
        <MapLegend 
          isVisible={showLegend}
          onToggle={() => setShowLegend(!showLegend)}
          legendItems={[
            { label: "FPS Plots", color: "#1E88E5", description: "Final Plot Survey - Blue outlines with land-use colors" },
            { label: "Survey Plots", color: "#27AE60", description: "DP Survey - Green outlines with zone colors (Zoom 12+)" },
            { label: "TPS Boundary", color: "#FF0000", description: "Town Planning Scheme" },
            { label: "Roads", color: "#95A5A6", description: "Road network" },
            { label: "Residential (R1-R5)", color: "#FFE4B5", description: "Residential zones" },
            { label: "Residential Affordable (RAH)", color: "#FFD4A3", description: "Affordable housing zones" },
            { label: "Gamtal & Village (GM/GME)", color: "#F5DEB3", description: "Village areas" },
            { label: "Agriculture (AG/A1/A2)", color: "#C8E6C9", description: "Agricultural zones" },
            { label: "Commercial (C/CBD)", color: "#FF6B6B", description: "Commercial & business" },
            { label: "Industrial (I/IG/GIDC)", color: "#9B59B6", description: "Industrial zones" },
            { label: "Institutional (I3/KZ)", color: "#3498DB", description: "Schools & institutions" },
            { label: "Logistics (L)", color: "#8D6E63", description: "Logistics zones" },
            { label: "Transit Oriented (TOD/TZ)", color: "#00BCD4", description: "Transit zones" },
            { label: "Parks & Recreation (P/RE)", color: "#27AE60", description: "Parks & open spaces" },
            { label: "Water Bodies", color: "#2196F3", description: "Lakes & water treatment" },
            { label: "Defence & Special", color: "#424242", description: "Special zones" }
          ]}
          title="Planning Zones"
        />
      </div>
      
      {/* Loading overlay */}
      {!isMapLoaded && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center bg-white p-8 rounded-3xl shadow-2xl border border-gray-100">
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className="absolute inset-0 border-4 border-gray-100 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-[#179246] rounded-full border-t-transparent animate-spin"></div>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-1">Loading Map Data</h3>
            <p className="text-sm text-gray-500">Preparing vector tiles and layers...</p>
          </div>
        </div>
      )}

      {/* FSI Analysis Modal */}
      <FSIAnalysisModal
        isOpen={showFSIModal}
        onClose={closeFSIModal}
        fsiData={fsiData}
        isLoading={isLoadingFSI}
        error={fsiError}
      />

      {/* Detailed FSI Analysis Modal */}
      <DetailedFSIModal
        isOpen={showDetailedFSIModal}
        onClose={closeDetailedFSIModal}
        fsiData={detailedFsiData}
        isLoading={isLoadingDetailedFSI}
        error={detailedFsiError}
      />

      {/* Custom FSI Report Wizard */}
      {showCustomFSIWizard && customFSIGid && (
        <CustomFSIReportWizard
          gid={customFSIGid}
          onClose={() => {
            setShowCustomFSIWizard(false);
            setCustomFSIGid(null);
          }}
        />
      )}
    </div>
  );
};

export default MapLibreMapPage;